import React, { useState, useEffect, useCallback } from 'react';
import {
  learnerApi,
  ExamAttemptListItem,
  ExamAttemptDetail,
  ExamKind,
} from '../../api/learner';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Loader2, Eye, ClipboardList } from 'lucide-react';
import { toast } from 'sonner';
import { Pagination } from '../../components/shared/Pagination';
import { ViewDialog, DetailSection, DetailItem } from '../../components/shared/ViewDialog';
import { ExportCsvButton } from '../../components/shared/ExportCsvButton';
import type { CsvColumn } from '../../lib/csv';

interface PageProps {
  isDark: boolean;
}

const PAGE_SIZE = 25;

const KIND_BADGE: Record<ExamKind, { label: string; cls: string }> = {
  mock: { label: 'Mock', cls: 'bg-[#FBF3D9] text-[#7A5E10] hover:bg-[#FBF3D9]' },
  retake: { label: 'Retake', cls: 'bg-[#FFFBEB] text-[#92400E] hover:bg-[#FFFBEB]' },
  wrong: { label: 'Wrong', cls: 'bg-[#FFE3E3] text-[#9F1A1A] hover:bg-[#FFE3E3]' },
  marathon: { label: 'Marathon', cls: 'bg-[#FBF3D9] text-[#7A5E10] hover:bg-[#FBF3D9]' },
  ticket: { label: 'Ticket', cls: 'bg-[#EAF5EE] text-[#1F6F4A] hover:bg-[#EAF5EE]' },
  topic: { label: 'Topic', cls: 'bg-[#EEF2FF] text-[#4F46E5] hover:bg-[#EEF2FF]' },
};

export default function ExamAttemptsPage({ isDark }: PageProps) {
  const [items, setItems] = useState<ExamAttemptListItem[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [kindFilter, setKindFilter] = useState<string>('all');
  const [passedFilter, setPassedFilter] = useState<string>('all');
  const [userIdFilter, setUserIdFilter] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [viewing, setViewing] = useState<ExamAttemptDetail | null>(null);
  const [viewOpen, setViewOpen] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, pageSize: PAGE_SIZE };
      if (kindFilter !== 'all') params.kind = kindFilter;
      if (passedFilter !== 'all') params.passed = passedFilter;
      if (userIdFilter.trim()) params.userId = userIdFilter.trim();
      if (from) params.from = new Date(from).toISOString();
      if (to) params.to = new Date(to).toISOString();
      const res = await learnerApi.listAttempts(params);
      setItems(res.items);
      setTotal(res.total);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Не удалось загрузить попытки');
    } finally {
      setLoading(false);
    }
  }, [page, kindFilter, passedFilter, userIdFilter, from, to]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleView = async (id: string) => {
    try {
      const detail = await learnerApi.getAttempt(id);
      setViewing(detail);
      setViewOpen(true);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Не удалось загрузить попытку');
    }
  };

  const csvColumns: CsvColumn<ExamAttemptListItem>[] = [
    { header: 'Начато', accessor: (r) => r.started_at },
    { header: 'Завершено', accessor: (r) => r.finished_at },
    { header: 'ID пользователя', accessor: (r) => r.user_id },
    { header: 'Вид', accessor: (r) => r.kind },
    { header: 'Режим', accessor: (r) => r.mode },
    { header: 'ID ссылки', accessor: (r) => r.ref_id },
    { header: 'Всего вопросов', accessor: (r) => r.total_questions },
    { header: 'Отвечено', accessor: (r) => r.answered_count },
    { header: 'Верных', accessor: (r) => r.correct_count },
    { header: 'Неверных', accessor: (r) => r.wrong_count },
    { header: 'Время (с)', accessor: (r) => r.time_spent_seconds },
    { header: 'Сдан', accessor: (r) => r.passed },
    { header: 'Брошен', accessor: (r) => r.abandoned },
  ];

  const fetchAllForExport = async (): Promise<ExamAttemptListItem[]> => {
    const rows: ExamAttemptListItem[] = [];
    const params: any = { pageSize: 100 };
    if (kindFilter !== 'all') params.kind = kindFilter;
    if (passedFilter !== 'all') params.passed = passedFilter;
    if (userIdFilter.trim()) params.userId = userIdFilter.trim();
    if (from) params.from = new Date(from).toISOString();
    if (to) params.to = new Date(to).toISOString();
    let pageN = 1;
    // hard cap: 50 pages × 100 = 5000 rows max per export
    while (pageN <= 50) {
      const res = await learnerApi.listAttempts({ ...params, page: pageN });
      rows.push(...res.items);
      if (!res.hasMore) break;
      pageN += 1;
    }
    return rows;
  };

  const formatDate = (s: string | null) => {
    if (!s) return '—';
    return new Date(s).toLocaleString('ru-RU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatDuration = (secs: number | null) => {
    if (secs == null) return '—';
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}m ${s}s`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-[#FBF3D9] flex items-center justify-center">
            <ClipboardList className="w-6 h-6 text-[#9C7E1B]" />
          </div>
          <div>
            <h1 className={cn(
              'text-2xl lg:text-3xl font-bold tracking-tight',
              isDark ? 'text-white' : 'text-[#111111]'
            )}>
              Попытки экзаменов
            </h1>
            <p className={cn('text-sm mt-0.5', isDark ? 'text-slate-400' : 'text-slate-500')}>
              Все попытки учеников, синхронизированные из приложения (mock / retake / wrong / marathon / ticket / topic)
            </p>
          </div>
        </div>
        <ExportCsvButton
          fetchRows={fetchAllForExport}
          columns={csvColumns}
          filename="exam_attempts"
          isDark={isDark}
        />
      </div>

      <Card className={cn(
        'p-4 rounded-2xl border-0',
        isDark ? 'bg-[#1A1A22] shadow-md shadow-black/30' : 'bg-white shadow-md shadow-black/[0.04]'
      )}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <Select value={kindFilter} onValueChange={(v) => { setKindFilter(v); setPage(1); }}>
            <SelectTrigger className="rounded-xl">
              <SelectValue placeholder="Тип" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Все типы</SelectItem>
              <SelectItem value="mock">Пробный</SelectItem>
              <SelectItem value="retake">Пересдача</SelectItem>
              <SelectItem value="wrong">Ошибки</SelectItem>
              <SelectItem value="marathon">Марафон</SelectItem>
              <SelectItem value="ticket">Билет</SelectItem>
              <SelectItem value="topic">Тема</SelectItem>
            </SelectContent>
          </Select>

          <Select value={passedFilter} onValueChange={(v) => { setPassedFilter(v); setPage(1); }}>
            <SelectTrigger className="rounded-xl">
              <SelectValue placeholder="Результат" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Все результаты</SelectItem>
              <SelectItem value="true">Сдал</SelectItem>
              <SelectItem value="false">Не сдал</SelectItem>
            </SelectContent>
          </Select>

          <Input
            placeholder="ID юзера (UUID)"
            value={userIdFilter}
            onChange={(e) => { setUserIdFilter(e.target.value); setPage(1); }}
            className="rounded-xl"
          />

          <Input
            type="date"
            value={from}
            onChange={(e) => { setFrom(e.target.value); setPage(1); }}
            className="rounded-xl"
            placeholder="С"
          />

          <Input
            type="date"
            value={to}
            onChange={(e) => { setTo(e.target.value); setPage(1); }}
            className="rounded-xl"
            placeholder="По"
          />
        </div>
      </Card>

      <Card className={cn(
        'rounded-2xl border-0 overflow-hidden',
        isDark ? 'bg-[#1A1A22] shadow-md shadow-black/30' : 'bg-white shadow-md shadow-black/[0.04]'
      )}>
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-[#C9A227]" />
          </div>
        ) : items.length === 0 ? (
          <div className={cn('text-center py-16 text-sm', isDark ? 'text-slate-400' : 'text-slate-500')}>
            Нет попыток по заданным фильтрам.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Начато</TableHead>
                <TableHead>Пользователь</TableHead>
                <TableHead>Тип</TableHead>
                <TableHead>Ссылка</TableHead>
                <TableHead>Балл</TableHead>
                <TableHead>Результат</TableHead>
                <TableHead>Время</TableHead>
                <TableHead className="text-right">Действие</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((a) => {
                const kindBadge = KIND_BADGE[a.kind];
                const score = a.total_questions > 0
                  ? `${a.correct_count}/${a.total_questions}`
                  : '—';
                return (
                  <TableRow key={a.id}>
                    <TableCell className="text-sm">{formatDate(a.started_at)}</TableCell>
                    <TableCell className="font-mono text-xs">{a.user_id.slice(0, 8)}…</TableCell>
                    <TableCell>
                      <Badge className={cn('rounded-lg font-semibold', kindBadge.cls)}>{kindBadge.label}</Badge>
                    </TableCell>
                    <TableCell className="text-sm">{a.ref_id ?? '—'}</TableCell>
                    <TableCell className="text-sm font-semibold">{score}</TableCell>
                    <TableCell>
                      {a.passed === true && <Badge className="bg-[#EAF5EE] text-[#1F6F4A] rounded-lg hover:bg-[#EAF5EE]">Сдал</Badge>}
                      {a.passed === false && <Badge className="bg-[#FFE3E3] text-[#9F1A1A] rounded-lg hover:bg-[#FFE3E3]">Не сдал</Badge>}
                      {a.passed === null && a.abandoned && <Badge variant="outline" className="rounded-lg">Брошено</Badge>}
                      {a.passed === null && !a.abandoned && <Badge variant="outline" className="rounded-lg">—</Badge>}
                    </TableCell>
                    <TableCell className="text-sm">{formatDuration(a.time_spent_seconds)}</TableCell>
                    <TableCell className="text-right">
                      <button
                        onClick={() => handleView(a.id)}
                        className="p-2 rounded-lg hover:bg-[#F5F3EF] dark:hover:bg-[#0F0F17] transition-colors"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}

        {totalPages > 1 && (
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            total={total}
            itemsPerPage={PAGE_SIZE}
            onPageChange={setPage}
            isDark={isDark}
          />
        )}
      </Card>

      <ViewDialog
        isOpen={viewOpen}
        onOpenChange={setViewOpen}
        title="Детали попытки"
        description="Полный разбор синхронизированной попытки ученика"
        isDark={isDark}
      >
        {viewing && (
          <div className="space-y-6">
            <DetailSection title="Обзор" isDark={isDark}>
              <DetailItem label="Тип" isDark={isDark}>{KIND_BADGE[viewing.kind].label}</DetailItem>
              <DetailItem label="Режим" isDark={isDark}>{viewing.mode ?? '—'}</DetailItem>
              <DetailItem label="ID ссылки" isDark={isDark}>{String(viewing.ref_id ?? '—')}</DetailItem>
              <DetailItem label="ID юзера" isDark={isDark} mono breakAll>{viewing.user_id}</DetailItem>
              <DetailItem label="Начато" isDark={isDark}>{formatDate(viewing.started_at)}</DetailItem>
              <DetailItem label="Завершено" isDark={isDark}>{formatDate(viewing.finished_at)}</DetailItem>
              <DetailItem label="Затрачено" isDark={isDark}>{formatDuration(viewing.time_spent_seconds)}</DetailItem>
              <DetailItem label="Балл" isDark={isDark}>{`${viewing.correct_count}/${viewing.total_questions}`}</DetailItem>
              <DetailItem label="Результат" isDark={isDark}>
                {viewing.passed === true ? 'Сдан' : viewing.passed === false ? 'Не сдан' : viewing.abandoned ? 'Брошен' : 'Не завершён'}
              </DetailItem>
            </DetailSection>

            <div>
              <h3 className={cn(
                'text-lg font-semibold mb-4',
                isDark ? 'text-white' : 'text-[#111111]'
              )}>
                Answers ({viewing.answers_log.length})
              </h3>
              <div className="space-y-1 text-xs max-h-[400px] overflow-y-auto">
                {viewing.answers_log.map((a, idx) => (
                  <div key={idx} className={cn(
                    'flex items-center justify-between p-2 rounded-lg',
                    a.is_correct
                      ? 'bg-[#EAF5EE]/50 dark:bg-[#1F6F4A]/15'
                      : 'bg-[#FFE3E3]/50 dark:bg-[#7F1D1D]/15'
                  )}>
                    <span className="font-mono">Q#{a.question_id}</span>
                    <span>
                      <span className={cn('font-bold mr-2', a.is_correct ? 'text-[#1F6F4A]' : 'text-[#9F1A1A]')}>
                        {a.selected || '—'}
                      </span>
                      {!a.is_correct && (
                        <span className="text-slate-500 dark:text-slate-400">→ {a.correct}</span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {viewing.client_meta && (
              <div>
                <h3 className={cn(
                  'text-lg font-semibold mb-4',
                  isDark ? 'text-white' : 'text-[#111111]'
                )}>
                  Метаданные клиента
                </h3>
                <pre className={cn(
                  'text-xs p-3 rounded-lg overflow-auto',
                  isDark ? 'bg-[#0F0F17] text-slate-300' : 'bg-[#F5F3EF] text-slate-700'
                )}>
                  {JSON.stringify(viewing.client_meta, null, 2)}
                </pre>
              </div>
            )}
          </div>
        )}
      </ViewDialog>
    </div>
  );
}
